<script setup lang="ts">
import { ApiPutInteractionEvent } from "@/apis/interaction";
import { ReplyEntityType } from "@/enums";
import { AIWriteType } from "@/enums/mateEnums";
import { InteractionEventStatus, InteractionEventType } from "@/enums/mission";
import api from "@/modules/api";
import { useGlobalModal } from "@/stores/globalModal";
import { useInteractiveReview } from "@/stores/interactive-review";
import { useMoment } from "@/stores/moment";
import { InteractionEvent, Moment } from "@/types/entity";
import { useDebounceFn } from "@vueuse/core";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  event: InteractionEvent;
  isNeedAction: boolean;
}>();

const reviewStore = useInteractiveReview();
const { t } = useI18n();

const entity = computed<Moment>(() => {
  return props.event.entity! as Moment;
});

const desc = computed<string>(() => {
  if (props.event.type === InteractionEventType.RequestMomentPost) {
    return t("interactive.reviewPage.moment.requestPost");
  } else if (props.event.type === InteractionEventType.RequestMomentComment) {
    return t("interactive.reviewPage.moment.requestComment");
  } else {
    return "";
  }
});

const rejectText = computed<string>(() => {
  if (props.event.type === InteractionEventType.RequestMomentPost) {
    return t("interactive.reviewPage.moment.requestPostRejected", {
      name: props.event.mate!.name,
    });
  } else if (props.event.type === InteractionEventType.RequestMomentComment) {
    return t("interactive.reviewPage.moment.requestCommentRejected", {
      name: props.event.mate!.name,
    });
  }
  return "";
});
const approveText = computed<string>(() => {
  if (props.event.type === InteractionEventType.RequestMomentPost) {
    return t("interactive.reviewPage.moment.requestPostApproved", {
      name: props.event.mate!.name,
    });
  } else if (props.event.type === InteractionEventType.RequestMomentComment) {
    return t("interactive.reviewPage.moment.requestCommentApproved", {
      name: props.event.mate!.name,
    });
  }
  return "";
});

const modalStore = useGlobalModal();
const clickEdit = () => {
  switch (props.event.type) {
    case InteractionEventType.RequestMomentComment:
      clickEditMomentComment();
      break;
    case InteractionEventType.RequestMomentPost:
      clickEditMomentPost();
      break;
  }
};

const clickEditMomentComment = () => {
  modalStore.openRichEditor({
    title: "编辑评论",
    content: props.event.content,
    aiType: AIWriteType.Reply,
    callback: (content) => {
      // 做一个截流
      if (content) {
        useDebounceFn(async () => {
          await ApiPutInteractionEvent(props.event.id, {
            content: content!,
          });
          const event = props.event;
          event.content = content!;
        }, 500)();
      }
    },
    options: {
      entityId: props.event.mateId,
      entityType: "mate",
      replyEntityId: props.event.entity?.id,
      replyEntityType: ReplyEntityType.Moment,
      parentId: (props.event.entity as Moment).replyId,
      content: props.event.content,
      mateId: props.event.mateId,
    },
  });
};

const clickEditMomentPost = () => {
  const entity = props.event.entity as Moment;
  modalStore.openRichEditor({
    title: "编辑随记",
    content: entity.text,
    aiType: AIWriteType.Moment,
    callback: (content) => {
      // 做一个截流
      console.log(content);
      entity.text = content!;
      if (content) {
        useDebounceFn(async () => {
          await api.updateMoment(entity.uuid, {
            text: content,
            mateId: entity.uuid,
            status: entity.status,
            visibility: entity.visibility,
          });
          entity.text = content;
        }, 500)();
      }
    },
    options: {
      mateId: entity.mate.id,
    },
  });
};
const momentStore = useMoment();
const agree = async () => {
  await reviewStore.reviewEvent(
    props.event.id,
    InteractionEventStatus.Approved
  );
  momentStore.initMomentListIfEmpty(true);
};
</script>
<template>
  <InteractionItemHeader
    :mate="event.mate!"
    :title="event.title"
    :desc="desc"
    icon="iconify mingcute--message-4-line"
    :dtCreate="event.dtCreate"
    :is-need-action="isNeedAction"
  />
  <div
    :class="{
      'text-primary-content': isNeedAction,
      'text-base-content': !isNeedAction,
    }"
    v-html="event.content"
  ></div>
  <div class="p-2 border rounded-lg bg-base-100/80">
    <ThreadItem1
      class="interaction-moment-item"
      :moment="entity"
      :show-interaction-area="false"
    />
  </div>
  <div
    v-if="event.reviewStatus === InteractionEventStatus.Pending"
    class="flex flex-col gap-2"
  >
    <div class="flex gap-2">
      <button
        class="btn btn-xs rounded"
        :class="{
          'text-primary-content border-primary-content bg-transparent':
            isNeedAction,
          'btn-outline': !isNeedAction,
        }"
        @click="agree"
      >
        {{ t("Buttons.agree") }}
      </button>
      <button
        class="btn btn-xs rounded"
        :class="{
          'text-primary-content border-primary-content bg-transparent':
            isNeedAction,
          'btn-outline': !isNeedAction,
        }"
        @click="clickEdit"
      >
        {{ t("game.modify") }}
      </button>
      <button
        class="btn btn-xs rounded"
        :class="{
          'text-primary-content border-primary-content bg-transparent':
            isNeedAction,
          'btn-outline': !isNeedAction,
        }"
        @click="
          reviewStore.reviewEvent(event.id, InteractionEventStatus.Rejected)
        "
      >
        {{ t("Buttons.deny") }}
      </button>
    </div>
  </div>
  <div v-else class="flex flex-col gap-2">
    <div
      v-if="event.reviewStatus === InteractionEventStatus.Rejected"
      class="text-error"
    >
      {{ rejectText }}
    </div>
    <div
      v-if="event.reviewStatus === InteractionEventStatus.Approved"
      class="text-success"
    >
      {{ approveText }}
    </div>
  </div>
</template>
<style>
.mail-preview.prose.prose-sm p {
  margin: 0;
}
.interaction-moment-item {
  border-bottom: none;
}
</style>
